'use client';

import { c as _c } from "react/compiler-runtime";
import { createOAuthUrl, encodeOAuthState } from '@core/utils';
import { useState } from 'react';
import { LOGIN_TYPE } from '@/api';
import { API_ROUTES, ENV, ROUTES, STORAGE_KEY } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useLoginUrlData } from '@/hooks/useLoginUrlData';
import { useUserAgentMobile } from '@/hooks/useUserAgentMobile';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import type { GTM_EVENT_TYPE, GtmEventParams } from '@/utils/analytics/gtm';
import { resolveAuthOrigin } from '@/utils/resolveAuthOrigin';
export type SocialLoginType = 'kakao' | 'naver' | 'google' | 'apple' | 'toss';

//* 카카오톡 앱 실행이 무산됐을 때 웹 인증으로 되돌리기까지 기다리는 시간.
//* 앱으로 전환되면 visibilitychange가, 페이지가 이동하면 pagehide가 폴백을 즉시 취소하므로
//* 정상 흐름의 체감 지연은 없다.
const KAKAO_APP_LAUNCH_FALLBACK_MS = 1200;
interface UseSocialLoginParams {
  type: SocialLoginType;
  redirectPath?: string;
  eventData?: GtmEventParams[GTM_EVENT_TYPE.SIGN_UP_STEP];
}
export const useSocialLogin = t0 => {
  const $ = _c(20);
  const {
    type,
    redirectPath,
    eventData
  } = t0;
  const {
    saveLoginUrlData
  } = useLoginUrlData();
  const {
    isWelloApp
  } = useCheckWelloApp();
  const isMobile = useUserAgentMobile();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: MESSAGE_KEYWORD.LOGIN
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    request: requestSnsLogin,
    isPending: isAppLoginPending
  } = useHandleApp(t1);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isPending = isAppLoginPending || isRedirecting;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      kakao: LOGIN_TYPE.KAKAO,
      naver: LOGIN_TYPE.NAVER,
      google: LOGIN_TYPE.GOOGLE,
      apple: LOGIN_TYPE.APPLE,
      toss: LOGIN_TYPE.TOSS
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const loginType = t2[type];
  let t3;
  if ($[2] !== type) {
    t3 = () => {
      location.href = createOAuthUrl({
        type,
        domain: resolveAuthOrigin(window.location.origin),
        redirectPath: ROUTES.AUTH.pathname,
        ...{
          kakao: {
            clientId: ENV.NEXT_PUBLIC_KAKAO_CLIENT_ID
          },
          naver: {
            clientId: ENV.NEXT_PUBLIC_NAVER_CLIENT_ID
          },
          google: {
            clientId: ENV.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
            scope: "openid https://www.googleapis.com/auth/userinfo.email"
          },
          apple: {
            clientId: ENV.NEXT_PUBLIC_APPLE_CLIENT_ID,
            scope: "email",
            appleApiRoutePath: API_ROUTES.APPLE_AUTH.pathname
          },
          toss: {
            clientId: ENV.NEXT_PUBLIC_TOSS_CLIENT_ID,
            scope: "user_ci user_birthday user_nationality user_name user_phone user_gender user_email"
          }
        }[type]
      });
    };
    $[2] = type;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const redirectToOauthPage = t3;
  let t4;
  if ($[4] !== isMobile || $[5] !== isWelloApp || $[6] !== loginType || $[7] !== redirectToOauthPage || $[8] !== requestSnsLogin || $[9] !== type) {
    t4 = () => {
      if (isWelloApp) {
        requestSnsLogin({
          loginType
        });
        return;
      }
      setIsRedirecting(true);
      if (type === "kakao" && isMobile) {
        const redirectUri = `${resolveAuthOrigin(window.location.origin)}${ROUTES.AUTH.pathname}`;
        const state = encodeOAuthState("kakao", ROUTES.AUTH.pathname);
        if (window.Kakao?.Auth?.authorize) {
          window.Kakao.Auth.authorize({
            redirectUri,
            state
          });
          const fallbackTimer = window.setTimeout(() => {
            if (document.visibilityState === "visible") {
              redirectToOauthPage();
            }
          }, KAKAO_APP_LAUNCH_FALLBACK_MS);
          const cancelFallback = () => window.clearTimeout(fallbackTimer);
          document.addEventListener("visibilitychange", cancelFallback, {
            once: true
          });
          window.addEventListener("pagehide", cancelFallback, {
            once: true
          });
          return;
        }
      }
      redirectToOauthPage();
    };
    $[4] = isMobile;
    $[5] = isWelloApp;
    $[6] = loginType;
    $[7] = redirectToOauthPage;
    $[8] = requestSnsLogin;
    $[9] = type;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const moveToOauth = t4;
  let t5;
  if ($[11] !== eventData || $[12] !== isPending || $[13] !== moveToOauth || $[14] !== redirectPath || $[15] !== saveLoginUrlData) {
    t5 = () => {
      if (isPending) {
        return;
      }
      if (eventData) {
        sessionStorage.setItem(STORAGE_KEY.IS_LOGIN_PROGRESS, "true");
        sessionStorage.setItem(STORAGE_KEY.SIGNUP_METHOD, eventData.method);
        sessionStorage.setItem(STORAGE_KEY.SIGNUP_TYPE, eventData.signup_type);
      }
      saveLoginUrlData(redirectPath);
      moveToOauth();
    };
    $[11] = eventData;
    $[12] = isPending;
    $[13] = moveToOauth;
    $[14] = redirectPath;
    $[15] = saveLoginUrlData;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const startLogin = t5;
  let t6;
  if ($[17] !== isPending || $[18] !== startLogin) {
    t6 = {
      startLogin,
      isPending
    };
    $[17] = isPending;
    $[18] = startLogin;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};